<template>
  <div class="space-y-20 lg:space-y-24 pb-20">
    <!-- ============ PRÓXIMAS FECHAS: Calendario Deportivo ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.proximasFechas')"
      as="section"
      id="próximas-fechas"
      :delay="100"
      class="px-6 pt-16 lg:px-16 lg:pt-24"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-7 h-7 sm:w-8 sm:h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span class="section-header__label text-lg font-normal"
            >Próximas Fechas</span
          >
        </div>
        <h2 class="text-2xl mb-2 md:text-4xl md:mb-0 font-semibold text-white">
          Calendario Deportivo
        </h2>
        <p class="text-white mb-10 text-lg font-normal sm:mb-8">
          No te pierdas ningún evento importante
        </p>

        <HomeSectionState
          v-if="calendarState !== 'ready'"
          :state="calendarState"
          empty-message="Todavía no hay torneos programados."
          loading-label="Cargando calendario"
          @retry="loadUpcomingEvents"
        />

        <div v-else>
          <!-- Calendario reutilizable (compacto para el home, full para /calendario) -->
          <CalendarioFbochas compact :events="upcoming" />
        </div>

        <div v-if="upcoming.length > 0" class="flex justify-center mt-6">
          <router-link to="/calendario" class="btn-bochas-rojo text-base">
            Ver calendario completo
          </router-link>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ COMPETENCIA: Resultados Recientes ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.resultados')"
      as="section"
      id="resultados"
      :delay="0"
      class="scroll-mt-24 px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="trophy" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Competencia</span>
        </div>
        <h2 class="text-2xl mb-2 md:text-4xl font-semibold text-white">
          Resultados Recientes
        </h2>
        <p class="text-white mb-10 text-lg font-normal lg:mb-8">
          Conocé a los últimos campeones de nuestras competencias
        </p>

        <HomeSectionState
          v-if="resultsState !== 'ready'"
          :state="resultsState"
          empty-message="Todavía no hay resultados publicados."
          loading-label="Cargando resultados"
          @retry="loadRecentResults"
        />

        <div v-else class="relative">
          <div
            ref="resultsScrollRef"
            class="home-results-scroll max-h-[480px] overflow-y-auto pr-1 sm:max-h-[680px] sm:pr-2"
            @scroll="updateResultsScrollHint"
          >
            <ResultadosAccordion
              :groups="recentResultGroups"
              @open-gallery="openGallery"
            />
          </div>
          <div
            v-if="showResultsScrollHint"
            class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-bochas-bg via-bochas-bg/90 to-transparent pb-3 pt-14"
          >
            <span class="rounded-full bg-white/10 px-3 py-1 text-xs text-white">
              Deslizá para ver más ↓
            </span>
          </div>
        </div>

        <div class="flex justify-center mt-14 sm:mt-6">
          <router-link to="/resultados" class="btn-bochas-rojo text-base">
            Ver historial completo
          </router-link>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ NOVEDADES: Últimas Noticias ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.novedades') && latestNews.length"
      as="section"
      id="novedades"
      :delay="200"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="newspaper" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Novedades</span>
        </div>
        <h2 class="text-2xl mb-2 md:text-4xl md:mb-0 font-semibold text-white">
          Últimas Noticias
        </h2>
        <p class="text-white mb-10">
          Mantenete al día con toda la actividad de la federación
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <article
            v-for="n in latestNews"
            :key="n.id"
            class="home-motion-card card-fbochas overflow-hidden flex flex-col"
          >
            <router-link :to="`/noticias/${n.id}`" class="flex flex-col h-full">
              <!-- Imagen de portada -->
              <div class="aspect-video overflow-hidden bg-bochas-bg">
                <img
                  v-if="n.cover_image"
                  :src="n.cover_image"
                  :alt="n.title"
                  class="home-motion-image w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-bochas-red-dark to-bochas-red-deep flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-12 h-12 text-white/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="1"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                </div>
              </div>

              <!-- Contenido -->
              <div class="p-5 flex-1 flex flex-col">
                <div class="flex items-center gap-2 mb-2 flex-wrap">
                  <span
                    v-if="n.tag"
                    class="text-[10px] sm:text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded"
                    :style="{
                      backgroundColor: n.tag.color || '#A52121',
                      color: 'white',
                    }"
                  >
                    {{ n.tag.name }}
                  </span>
                  <time
                    v-if="n.published_at"
                    class="text-xs text-bochas-text-muted"
                  >
                    {{ formatDate(n.published_at) }}
                  </time>
                </div>
                <h3 class="font-normal text-lg mb-2 text-white line-clamp-2">
                  {{ n.title }}
                </h3>
                <p
                  class="text-sm text-bochas-text-muted mb-4 line-clamp-3 flex-1"
                >
                  {{ n.description || textPreview(n.content) }}
                </p>
                <span class="text-bochas-rojo text-xs sm:text-sm font-semibold">
                  Leer más →
                </span>
              </div>
            </router-link>
          </article>
        </div>

        <div
          v-if="latestNews.length > 0"
          class="flex justify-center mt-10 sm:mt-6"
        >
          <router-link to="/noticias" class="btn-bochas-rojo text-base">
            Ver todas las noticias
          </router-link>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ LA COMUNIDAD: Unidos por el deporte ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.comunidad') && community.stats.length"
      as="section"
      id="comunidad"
      :delay="300"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="users" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">La Comunidad</span>
        </div>
        <h2 class="text-2xl mb-2 md:text-4xl md:mb-0 font-semibold text-white">
          Unidos por el deporte
        </h2>
        <p class="text-white text-lg font-normal mb-10 sm:mb-8">
          Una red de clubes y jugadores que crece en toda la provincia
        </p>

        <div class="grid grid-cols-1 gap-4 mb-10 md:grid-cols-3 lg:gap-6">
          <div
            v-for="(s, idx) in community.stats"
            :key="s.label"
            class="community-stat card-fbochas flex flex-row items-center justify-left lg:justify-center gap-5 px-6 py-8 sm:p-8 text-left"
          >
            <div
              class="community-stat__icon w-16 h-16 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center"
              :class="idx === 1 ? 'bg-bochas-azul' : 'bg-bochas-rojo'"
            >
              <IconFbochas :name="s.icon" :size="30" color="#fff" />
            </div>
            <div
              class="flex flex-col md:flex-row gap-3 items-left lg:items-center justify-center"
            >
              <p class="text-4xl font-bold text-white">{{ s.value }}</p>
              <p class="text-white text-lg sm:text-sm">{{ s.label }}</p>
            </div>
          </div>
        </div>

        <div class="community-map-frame grid grid-cols-1">
          <MapaComunidad />
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ HISTORIAS: Testimonio ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.historias') && hasHistoriaContent"
      as="section"
      id="historias"
      :delay="400"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="mensaje" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Historias</span>
        </div>
        <h2 class="text-2xl mb-2 md:text-4xl md:mb-0 font-semibold text-white">
          {{ historiaTitle }}
        </h2>
        <p class="text-bochas-text-muted text-lg mb-10 sm:mb-8">
          {{ historiaSubtitle }}
        </p>

        <div
          class="home-banner home-story-banner relative rounded-xl overflow-hidden bg-black min-h-[600px] md:min-h-[450px] 2xl:min-h-[600px] flex flex-col justify-end"
        >
          <!-- Imagen configurada o portada del contenido publicado -->
          <img
            v-if="storyImage"
            :src="storyImage"
            :alt="`Imagen para la historia de ${historia.author}`"
            class="home-banner-image absolute inset-0 w-full h-full object-cover"
          />
          <!-- Overlay oscuro para legibilidad del texto blanco -->
          <div
            class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20"
          ></div>

          <!-- Contenido sobre la imagen -->
          <div class="relative p-6 sm:p-12 md:p-20">
            <blockquote
              class="text-3xl md:text-4xl font-bold italic text-white mb-6 leading-tight"
            >
              "{{ historia.quote }}"
            </blockquote>
            <div class="mb-6 text-end">
              <p class="font-normal text-white text-xl sm:text-4xl">
                {{ historia.author }}
              </p>
              <p class="text-white font-medium text-xl sm:text-2xl">
                {{ historiaAuthorSubtitle }}
              </p>
            </div>
          </div>
        </div>
        <div class="flex justify-center mt-10 sm:mt-8">
          <router-link
            to="/historias"
            class="home-action inline-block bg-bochas-rojo hover:bg-bochas-rojo/85 text-white font-semibold px-6 py-2.5 rounded-full transition-colors"
          >
            Descubrir historias
          </router-link>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ ENVIAR HISTORIA ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.enviarHistoria') && hasHistoriaContent"
      as="section"
      id="enviarHistoria"
      :delay="500"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div
          class="home-banner p-8 rounded-3xl bg-degradado-h-bochas flex flex-col md:flex-column items-center justify-between gap-8"
        >
          <div>
            <h3
              class="text-3xl text-center sm:text-4xl font-bold flex justify-center items-center text-white"
            >
              ¿Tenés una historia para compartir?
            </h3>
            <p
              class="text-white text-center text-xl sm:text-2xl font-medium mt-2 sm:mt-4"
            >
              Queremos conocer tu experiencia en las bochas. Cada historia suma
              a nuestra comunidad.
            </p>
          </div>
          <router-link
            to="/historias"
            class="home-action bg-azul hover:bg-bochas-accent/80 text-white text-xl sm:text-2xl font-medium px-10 py-4 rounded-full"
          >
            Enviar mi Historia
          </router-link>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ FUTURO: Jóvenes promesas ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.futuro') && hasFutureContent"
      as="section"
      id="futuro"
      :delay="600"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="futuro" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Futuro</span>
        </div>
        <div
          class="home-banner card-fbochas lg:h-[400px] grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 items-start overflow-hidden p-0 md:p-6"
        >
          <div
            class="order-2 h-full p-4 pt-5 sm:p-6 md:order-1 md:p-0 flex flex-col"
          >
            <h2
              class="text-3xl sm:text-4xl font-semibold text-white mb-3 sm:mb-6 leading-tight"
            >
              {{ futureTitle }}
            </h2>
            <p
              class="text-base sm:text-2xl font-medium text-[#99A1AF] mb-5 sm:mb-6 leading-snug"
            >
              {{ futureSubtitle }}
            </p>

            <!-- Stats reutilizables (StatCard component) -->
            <!-- <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <StatCard :value="200" label="Participantes" />
              <StatCard :value="20" label="Asociaciones" />
              <StatCard :value="15" label="Torneos" />
            </div> -->

            <router-link
              to="/futuro"
              class="btn-bochas-rojo text-sm mt-auto inline-block sm:w-auto md:w-fit text-center"
            >
              Conocer más
            </router-link>
          </div>

          <!-- Imagen configurada o del contenido publicado -->
          <div
            class="order-1 aspect-video overflow-hidden rounded-t-xl bg-bochas-bg relative md:order-2 md:h-full md:aspect-auto md:rounded-xl md:border md:border-white/10"
          >
            <img
              v-if="futureImage"
              :src="futureImage"
              alt="Jóvenes Bochófilos jugando bochas"
              class="home-banner-image absolute inset-0 w-full h-full object-cover"
            />
            <div v-else class="w-full h-full bg-degradado-bochas"></div>
          </div>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ PASADO: Referentes históricos ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.pasado') && hasPastContent"
      as="section"
      id="pasado"
      :delay="700"
      class="px-6 lg:px-16 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="pasado" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Pasado</span>
        </div>
        <div
          class="home-banner card-fbochas lg:h-[400px] grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-10 items-start overflow-hidden p-0 md:p-6"
        >
          <div
            class="order-2 h-full p-4 pt-5 sm:p-6 md:order-2 md:p-0 flex flex-col"
          >
            <h2
              class="text-3xl sm:text-3xl 2xl:text-4xl font-semibold text-white mb-3 sm:mb-6 leading-tight"
            >
              {{ pastTitle }}
            </h2>
            <p
              class="text-base sm:text-2xl font-medium text-[#99A1AF] mb-5 sm:mb-6 leading-snug"
            >
              {{ pastSubtitle }}
            </p>

            <!-- Stats reutilizables (StatCard component) -->
            <!-- <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <StatCard
                :value="200"
                label="Referentes cordobeses"
                icon="location"
              />
              <StatCard :value="30" label="Clubes históricos" icon="location" />
               <StatCard
                :value="50"
                label="Décadas de historia"
                icon="location"
              /> 
            </div> -->

            <router-link
              to="/pasado"
              class="btn-bochas-azul text-sm mt-auto inline-block sm:w-auto md:w-fit text-center"
            >
              Conocer más
            </router-link>
          </div>

          <!-- Imagen configurada o del contenido publicado -->
          <div
            class="order-1 aspect-video overflow-hidden rounded-t-xl bg-bochas-bg relative md:order-1 md:h-full md:aspect-auto md:rounded-xl md:border md:border-white/10"
          >
            <img
              v-if="pastImage"
              :src="pastImage"
              alt="Jugadores históricos cordobeses"
              class="home-banner-image absolute top-0 left-0 w-full h-full object-cover"
            />
            <div
              v-else
              class="absolute top-0 left-0 w-full h-full bg-degradado-bochas"
            ></div>
          </div>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ ALOJAMIENTO: Servicios para la comunidad ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.alojamiento') && hasAlojamientoContent"
      as="section"
      id="alojamiento"
      :delay="800"
      class="relative overflow-hidden px-6 lg:px-0 lg:pt-0"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="alojamiento" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Alojamiento</span>
        </div>
      </div>
      <div class="max-w-full mx-auto">
        <div
          class="home-banner home-alojamiento-banner relative max-w-full mx-auto overflow-hidden min-h-[480px] lg:min-h-[600px] bg-black"
        >
          <!-- Imagen configurada desde administración -->
          <img
            v-if="alojamientoImage"
            :src="alojamientoImage"
            alt=""
            class="home-banner-image absolute inset-0 w-full h-full object-cover rounded-3xl sm:rounded-xl lg:rounded-none"
          />
          <div v-else class="absolute inset-0 bg-degradado-bochas"></div>
          <!-- Overlay oscuro horizontal (más oscuro a la izquierda dónde está el texto) -->
          <div
            class="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent"
          ></div>

          <!-- Contenido por encima -->
          <div
            class="relative z-10 p-6 sm:p-8 md:p-10 lg:px-16 lg:py-20 flex flex-col justify-end min-h-[480px] lg:min-h-[600px]"
          >
            <h2
              class="text-3xl md:text-4xl max-w-2xl font-bold text-white mb-6"
            >
              {{ alojamientoTitle }}
            </h2>
            <p
              class="text-white mb-8 text-base max-w-7xl font-medium sm:text-2xl"
            >
              {{ alojamientoSubtitle }}
            </p>

            <div class="flex flex-wrap sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              <div
                v-for="(service, idx) in alojamiento.services"
                :key="service.title"
                :class="[
                  'flex flex-col items-start gap-3 border-bochas-accent/50 p-4 rounded-xl',
                  idx > 0 ? 'sm:pl-6 sm:border-l sm:border-white/80' : '',
                ]"
              >
                <IconFbochas
                  :name="service.icon"
                  :size="60"
                  :color="
                    service.color === 'azul'
                      ? '#1d527c'
                      : service.color === 'rojo'
                      ? '#a52121'
                      : '#fff'
                  "
                />
                <h3
                  class="text-lg sm:text-3xl font-semibold text-white text-center"
                >
                  {{ service.title }}
                </h3>
              </div>
            </div>

            <button
              v-if="alojamiento.services?.length"
              @click="$router.push('/alojamiento')"
              class="home-action bg-bochas-azul hover:bg-bochas-accent/80 text-white font-semibold px-10 py-4 rounded-full w-52 text-center"
            >
              {{ alojamiento.services[0].cta }}
            </button>
          </div>
        </div>
      </div>
    </RevealOnScroll>

    <!-- ============ SPONSORS (oculto) ============ -->
    <RevealOnScroll
      v-if="sectionVisible('home.sponsors') && sponsors.length"
      as="section"
      id="sponsors"
      :delay="900"
      class="px-6 lg:px-16 lg:pt-0 hidden"
    >
      <div class="max-w-7xl mx-auto">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="acompaniamiento" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Acompañamiento</span>
        </div>
        <h2 class="text-3xl md:text-4xl font-semibold text-white mb-2">
          Sponsors y Aliados
        </h2>
        <p class="text-white text-lg font-normal mb-8">
          Agradecemos el apoyo de nuestros sponsors
        </p>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            v-for="s in sponsors"
            :key="s.id"
            class="home-sponsor card-fbochas aspect-video flex items-center justify-center bg-bochas-gold/20"
          >
            <span class="font-bold text-bochas-red-dark">{{ s.name }}</span>
          </div>
        </div>
      </div>
    </RevealOnScroll>

    <ResultadosGallery
      :open="galleryOpen"
      :images="galleryImages"
      :index="galleryIndex"
      @update:index="galleryIndex = $event"
      @close="closeGallery"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import IconFbochas from "../Icons/IconFbochas.vue";
import ResultadosAccordion from "../Resultados/ResultadosAccordion.vue";
import ResultadosGallery from "../Resultados/ResultadosGallery.vue";
import MapaComunidad from "../Mapa/MapaComunidad.vue";
import CalendarioFbochas from "../Calendario/CalendarioFbochasComponent.vue";
import StatCard from "../Common/StatCard.vue";
import RevealOnScroll from "../Animations/RevealOnScroll.vue";
import HomeSectionState from "./HomeSectionState.vue";
import {
  createCommunityFallback,
  getCommunityStats,
} from "@/services/landing/landingService";
import { listarTorneos } from "@/services/tournaments/tournamentsService";
import { listarEventos } from "@/services/appointments/appointmentsService";
import { listarNoticias } from "@/services/noticias/noticiasService";
import { listarJovenes } from "@/services/youngPlayers/youngPlayersService";
import { listarReferentes } from "@/services/referentes/referentesService";
import { obtenerSiteMediaMap } from "@/services/siteMedia/siteMediaService";
import { obtenerSiteSettingsMap } from "@/services/siteSettings/siteSettingsService";
import { getSiteMediaFallback } from "@/data/siteMediaFallbacks";
import {
  crearSiteVisibility,
  obtenerSiteVisibility,
} from "@/services/siteSettings/siteVisibilityService";
import { formatDate } from "../../utils/date";

const recentResults = ref([]);
const upcoming = ref([]);
const resultsScrollRef = ref(null);
const showResultsScrollHint = ref(false);
const resultsState = ref("loading");
const calendarState = ref("loading");
const homeSectionStorageKey = "web-bochas-home-section";
const homeSectionTopOffset = 88;
let homeSectionObserver = null;
let resultsResizeObserver = null;
const siteVisibility = ref(crearSiteVisibility());
const sectionPageDependencies = {
  "home.resultados": "pages.resultados",
  "home.proximasFechas": "pages.calendario",
  "home.novedades": "pages.noticias",
  "home.historias": "pages.historias",
  "home.enviarHistoria": "pages.historias",
  "home.futuro": "pages.futuro",
  "home.pasado": "pages.pasado",
  "home.alojamiento": "pages.alojamiento",
};

function sectionVisible(key) {
  const pageKey = sectionPageDependencies[key];
  return (
    siteVisibility.value[key] !== false &&
    (!pageKey || siteVisibility.value[pageKey] !== false)
  );
}

// === Lightbox de galería (home) ===
const galleryOpen = ref(false);
const galleryImages = ref([]);
const galleryIndex = ref(0);

function openGallery(result) {
  // El resultado es un item aplanado (flatItem) que tiene `images` del torneo padre
  galleryImages.value = (result.images || []).map((i) => i.url);
  galleryIndex.value = 0;
  galleryOpen.value = true;
  document.body.style.overflow = "hidden";
}

function updateResultsScrollHint() {
  const element = resultsScrollRef.value;
  showResultsScrollHint.value = Boolean(
    element &&
      element.scrollHeight > element.clientHeight + 2 &&
      element.scrollTop + element.clientHeight < element.scrollHeight - 2,
  );
}
function closeGallery() {
  galleryOpen.value = false;
  document.body.style.overflow = "";
}
const latestNews = ref([]);
const latestHistorias = ref([]);
const jóvenes = ref([]);
const referentes = ref([]);
const community = ref(createCommunityFallback());
const homeMediaReady = ref(true);
const historia = ref({
  title: "",
  subtitle: "",
  quote: "",
  author: "",
  authorSubtitle: "",
  club: "",
  years: "",
});

const historiaAuthorSubtitle = computed(() => {
  const parts = [historia.value.club, historia.value.years].filter(Boolean);
  return parts.length ? parts.join(" - ") : historia.value.authorSubtitle;
});

const historiaTitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return historia.value.title || "Historias de nuestra cancha";
});

const historiaSubtitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return (
    historia.value.subtitle || "Voces de quienes viven y sienten las bochas."
  );
});

const storyImage = ref(getSiteMediaFallback("home.historias"));
const futureImage = ref(getSiteMediaFallback("home.jovenes"));
const futureMedia = ref(null);
const pastImage = ref(getSiteMediaFallback("home.historicos"));
const pastMedia = ref(null);
const alojamientoImage = ref(getSiteMediaFallback("home.alojamiento"));
const alojamientoMedia = ref(null);

function mediaHasContent(item) {
  return Boolean(item?.imageUrl || item?.title || item?.subtitle);
}

const hasHistoriaContent = computed(() =>
  Boolean(
    latestHistorias.value.length ||
      storyImage.value ||
      historia.value.quote ||
      historia.value.author,
  ),
);
const hasFutureContent = computed(
  () => jóvenes.value.length > 0 || mediaHasContent(futureMedia.value),
);
const hasPastContent = computed(
  () => referentes.value.length > 0 || mediaHasContent(pastMedia.value),
);
const hasAlojamientoContent = computed(() =>
  Boolean(
    mediaHasContent(alojamientoMedia.value) ||
      alojamiento.value.title ||
      alojamiento.value.description ||
      alojamiento.value.services.length,
  ),
);

const futureTitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return (
    futureMedia.value?.title ||
    (jóvenes.value.length ? "Jóvenes Bochófilos" : "")
  );
});

const futureSubtitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return futureMedia.value?.subtitle;
});

const pastTitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return (
    pastMedia.value?.title ||
    (referentes.value.length ? "Jugadores históricos" : "")
  );
});

const pastSubtitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return pastMedia.value?.subtitle;
});

const alojamientoTitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return alojamientoMedia.value?.title || alojamiento.value.title;
});

const alojamientoSubtitle = computed(() => {
  if (!homeMediaReady.value) return "";
  return alojamientoMedia.value?.subtitle || alojamiento.value.description;
});

// Mapea el campo `color` del JSON de servicios a un color hex para los iconos.
function colorToHex(color) {
  if (color === "azul") return "#1d527c";
  if (color === "rojo") return "#a52121";
  return ""; // default: currentColor
}
const alojamiento = ref({ title: "", description: "", services: [] });
const sponsors = ref([]);

function normalizeResultDate(date) {
  return String(date || "").split("T")[0];
}

function weekendGroupKey(item) {
  const values = (item.dates || [])
    .map((date) => date.event_date || date)
    .filter(Boolean)
    .sort();
  const source = values[0] || item.event_date;
  const [y, m, d] = normalizeResultDate(source)
    .split("-")
    .map((n) => parseInt(n, 10));
  if (!y || !m || !d) return normalizeResultDate(item.event_date);
  const date = new Date(y, m - 1, d);
  const dow = date.getDay();
  const start = dow === 5 ? date : new Date(y, m - 1, d - ((dow + 2) % 7));
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(start.getDate()).padStart(2, "0")}`;
}

const recentResultGroups = computed(() => {
  const items = [];
  for (const t of recentResults.value) {
    const cats = t.categories || [];
    const catsWithPodium = cats
      .filter((c) => (c.podiums || []).length > 0)
      .sort((a, b) => {
        if (a.display_order === 999 && b.display_order !== 999) return -1;
        if (a.display_order !== 999 && b.display_order === 999) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      });
    if (catsWithPodium.length === 0) {
      items.push({
        id: t.id,
        tournamentId: t.id,
        tournamentUuid: t.uuid,
        event_date: t.event_date,
        name: t.name,
        categoryId: null,
        categoryName: "",
        categoryCity: "",
        categoryDisplayOrder: null,
        association: t.association,
        location: t.location,
        description: t.description,
        date_range: t.date_range || "",
        weekend_label: t.weekend_label || "",
        dates: t.dates || [],
        images: t.images || [],
        cover_image: t.cover_image,
        isFinal: false,
        categories: [],
      });
      continue;
    }
    const final = catsWithPodium.find((c) => c.display_order === 999);
    const mainCategory =
      final ||
      catsWithPodium.slice().sort((a, b) => {
        return (
          new Date(b.category_date || 0).getTime() -
          new Date(a.category_date || 0).getTime()
        );
      })[0];
    if (!mainCategory) continue;
    items.push({
      id: t.id,
      tournamentId: t.id,
      tournamentUuid: t.uuid,
      event_date: mainCategory.category_date || t.event_date,
      name: t.name,
      categoryId: mainCategory.id,
      categoryName: catsWithPodium.length === 1 ? mainCategory.name : "",
      categoryCity: catsWithPodium.length === 1 ? mainCategory.city : "",
      categoryDisplayOrder: mainCategory.display_order,
      association: t.association,
      location: t.location,
      description: t.description,
      date_range: t.date_range || "",
      weekend_label: t.weekend_label || "",
      dates: t.dates || [],
      images: t.images || [],
      cover_image: t.cover_image,
      isFinal: !!final,
      categories: catsWithPodium,
    });
  }
  // Agrupar por fin de semana cuando el backend envía el rango nuevo.
  const groups = new Map();
  for (const item of items) {
    const key = weekendGroupKey(item);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  // Ordenar cada grupo: Final primero, luego por nombre del torneo
  for (const arr of groups.values()) {
    arr.sort((a, b) => {
      if (a.isFinal !== b.isFinal) return a.isFinal ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }
  return Array.from(groups, ([label, items]) => ({
    date: normalizeResultDate(items[0]?.event_date),
    label: items[0]?.weekend_label ? label : "",
    items,
  }));
});

// El calendario (grid + lista + modal) lo maneja el componente compartido
// CalendarioFbochas. Solo necesitamos pasarle `upcoming` y opcionalmente `compact`.

// Helper: extrae texto plano de un HTML para preview rápido
function textPreview(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const txt = tmp.textContent || tmp.innerText || "";
  return txt.slice(0, 180) + (txt.length > 180 ? "..." : "");
}

async function loadRecentResults() {
  if (!recentResults.value.length) resultsState.value = "loading";
  try {
    const data = await listarTorneos({ limit: 50 });
    recentResults.value = data?.items || [];
    resultsState.value = recentResults.value.length ? "ready" : "empty";
  } catch (_) {
    resultsState.value = recentResults.value.length ? "ready" : "error";
  }
}

async function loadUpcomingEvents() {
  if (!upcoming.value.length) calendarState.value = "loading";
  try {
    const data = await listarEventos();
    upcoming.value = data?.items || [];
    calendarState.value = upcoming.value.length ? "ready" : "empty";
  } catch (_) {
    calendarState.value = upcoming.value.length ? "ready" : "error";
  }
}

const homeSectionIds = [
  "resultados",
  "próximas-fechas",
  "novedades",
  "comunidad",
  "historias",
  "enviarHistoria",
  "futuro",
  "pasado",
  "alojamiento",
];

function saveVisibleHomeSection() {
  const viewportAnchor = window.innerHeight * 0.35;
  let activeId = homeSectionIds[0];
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const id of homeSectionIds) {
    const section = document.getElementById(id);
    if (!section) continue;
    const rect = section.getBoundingClientRect();
    const distance = Math.abs(rect.top - viewportAnchor);
    if (
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      distance < closestDistance
    ) {
      activeId = id;
      closestDistance = distance;
    }
  }

  sessionStorage.setItem(homeSectionStorageKey, activeId);
}

function restoreHomeSection() {
  const sectionId = sessionStorage.getItem(homeSectionStorageKey);
  const section = sectionId ? document.getElementById(sectionId) : null;
  if (!section) return;
  const top = section.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({
    top: Math.max(0, top - homeSectionTopOffset),
    left: 0,
    behavior: "auto",
  });
}

function restoreHomeSectionStable() {
  restoreHomeSection();
  window.setTimeout(restoreHomeSection, 120);
  window.setTimeout(restoreHomeSection, 360);
}

function startHomeSectionObserver() {
  if (!("IntersectionObserver" in window)) {
    window.addEventListener("scroll", saveVisibleHomeSection, {
      passive: true,
    });
    return;
  }

  homeSectionObserver = new IntersectionObserver(
    (entries) => {
      const active = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (active?.target?.id) {
        sessionStorage.setItem(homeSectionStorageKey, active.target.id);
      }
    },
    { rootMargin: "-30% 0px -45% 0px", threshold: [0, 0.25, 0.5, 0.75] },
  );

  for (const id of homeSectionIds) {
    const section = document.getElementById(id);
    if (section) homeSectionObserver.observe(section);
  }
}

onMounted(async () => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  try {
    siteVisibility.value = await obtenerSiteVisibility();
  } catch (_) {}

  const operationalSections = Promise.allSettled([
    loadRecentResults(),
    loadUpcomingEvents(),
  ]);

  const [n, hist, j, rf, c, media, settings] = await Promise.allSettled([
    listarNoticias({ limit: 3, type: "NOTICIA" }),
    listarNoticias({ limit: 3, type: "HISTORIA" }),
    listarJovenes(),
    listarReferentes(),
    getCommunityStats(),
    obtenerSiteMediaMap(),
    obtenerSiteSettingsMap(),
  ]);

  await operationalSections;
  if (n.status === "fulfilled") {
    latestNews.value = n.value?.items || [];
  }
  if (hist.status === "fulfilled") {
    latestHistorias.value = hist.value?.items || [];
  }
  if (j.status === "fulfilled") {
    jóvenes.value = j.value?.items || [];
  }
  if (rf.status === "fulfilled") {
    referentes.value = rf.value?.items || [];
  }
  if (c.status === "fulfilled") community.value = c.value;

  if (
    settings.status === "fulfilled" &&
    settings.value["alojamiento.content"]
  ) {
    try {
      const content = JSON.parse(settings.value["alojamiento.content"]);
      alojamiento.value = {
        title: content.heroTitle || "",
        description: content.heroSubtitle || "",
        services: [
          content.albergueTitle
            ? {
                icon: "albergue",
                title: content.albergueTitle,
                cta: "Conocé más",
                color: "azul",
              }
            : null,
          content.casaTitle
            ? {
                icon: "casa",
                title: content.casaTitle,
                cta: "Conocé más",
                color: "rojo",
              }
            : null,
        ].filter(Boolean),
      };
    } catch (_) {
      alojamiento.value = { title: "", description: "", services: [] };
    }
  }

  const mediaMap = media.status === "fulfilled" ? media.value || {} : {};
  const homeHistorias = mediaMap["home.historias"];
  const featuredHistoria = latestHistorias.value[0];
  storyImage.value =
    homeHistorias?.imageUrl ||
    featuredHistoria?.cover_image ||
    getSiteMediaFallback("home.historias");
  if (homeHistorias) {
    historia.value = {
      ...historia.value,
      title: homeHistorias.title || historia.value.title,
      subtitle: homeHistorias.subtitle || historia.value.subtitle,
      quote: homeHistorias.quote || historia.value.quote,
      author: homeHistorias.author || historia.value.author,
      club: homeHistorias.club || historia.value.club,
      years: homeHistorias.years || historia.value.years,
      authorSubtitle:
        homeHistorias.authorSubtitle || historia.value.authorSubtitle,
    };
  } else if (featuredHistoria) {
    historia.value = {
      ...historia.value,
      title: featuredHistoria.title || "",
      subtitle: featuredHistoria.description || "",
      quote: textPreview(
        featuredHistoria.description || featuredHistoria.content,
      ),
    };
  }
  futureImage.value =
    mediaMap["home.jovenes"]?.imageUrl ||
    jóvenes.value[0]?.photo_url ||
    getSiteMediaFallback("home.jovenes");
  futureMedia.value = mediaMap["home.jovenes"] || null;
  pastImage.value =
    mediaMap["home.historicos"]?.imageUrl ||
    referentes.value[0]?.photo_url ||
    getSiteMediaFallback("home.historicos");
  pastMedia.value = mediaMap["home.historicos"] || null;
  alojamientoImage.value =
    mediaMap["home.alojamiento"]?.imageUrl ||
    getSiteMediaFallback("home.alojamiento");
  alojamientoMedia.value = mediaMap["home.alojamiento"] || null;
  homeMediaReady.value = true;

  await nextTick();
  updateResultsScrollHint();
  if ("ResizeObserver" in window && resultsScrollRef.value?.firstElementChild) {
    resultsResizeObserver = new ResizeObserver(updateResultsScrollHint);
    resultsResizeObserver.observe(resultsScrollRef.value.firstElementChild);
  }
  startHomeSectionObserver();
});

onBeforeUnmount(() => {
  saveVisibleHomeSection();
  window.removeEventListener("scroll", saveVisibleHomeSection);
  if (homeSectionObserver) {
    homeSectionObserver.disconnect();
    homeSectionObserver = null;
  }
  if (resultsResizeObserver) {
    resultsResizeObserver.disconnect();
    resultsResizeObserver = null;
  }
});
</script>

<style scoped>
.home-results-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.24) transparent;
}

.home-results-scroll::-webkit-scrollbar {
  width: 6px;
}

.home-results-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.home-results-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.24);
}

.community-stat {
  transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 300ms ease, box-shadow 300ms ease;
}

.community-stat__icon {
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 300ms ease;
}

.community-stat:hover {
  transform: translateY(-6px);
  border-color: rgba(165, 33, 33, 0.48);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.25);
}

.community-stat:hover .community-stat__icon {
  transform: translateY(-3px) rotate(-4deg) scale(1.08);
  filter: brightness(1.12);
}

.community-map-frame {
  border-radius: 0.75rem;
  transition: box-shadow 350ms ease, filter 350ms ease;
}

.community-map-frame:hover {
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
  filter: brightness(1.025);
}

.home-action,
.btn-bochas-rojo,
.btn-bochas-azul,
.btn-bochas-trans {
  position: relative;
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 240ms ease, filter 240ms ease, background-color 240ms ease;
}

.home-action:hover,
.btn-bochas-rojo:hover,
.btn-bochas-azul:hover,
.btn-bochas-trans:hover {
  transform: translateY(-3px) scale(1.015);
  filter: brightness(1.08);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.28);
}

.home-action:active,
.btn-bochas-rojo:active,
.btn-bochas-azul:active,
.btn-bochas-trans:active {
  transform: translateY(0) scale(0.96);
}

.home-banner {
  transition: transform 380ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 380ms ease, border-color 380ms ease;
}

.home-banner-image {
  transition: transform 900ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease;
}

.home-banner:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.24);
}

.home-banner:hover .home-banner-image {
  transform: scale(1.025);
  filter: brightness(1.04);
}

/* Evita la línea de fondo que produce el recorte subpíxel al escalar
   una imagen absoluta dentro del banner redondeado de Historias. */
.home-story-banner:hover {
  transform: none;
}

.home-story-banner:hover .home-banner-image {
  transform: none;
  filter: brightness(1.035);
}

.home-alojamiento-banner:hover {
  transform: none;
}

.home-alojamiento-banner:hover .home-banner-image {
  transform: none;
  filter: brightness(1.035);
}

.home-motion-card {
  transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 300ms ease, box-shadow 300ms ease;
}

.home-motion-image {
  transition: transform 550ms cubic-bezier(0.22, 1, 0.36, 1), filter 400ms ease;
}

.home-motion-card:hover {
  transform: translateY(-6px);
  border-color: rgba(165, 33, 33, 0.55);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
}

.home-motion-card:hover .home-motion-image {
  transform: scale(1.045);
  filter: brightness(1.08);
}

.home-sponsor {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 260ms ease, background-color 260ms ease;
}

.home-sponsor:hover {
  transform: translateY(-4px) scale(1.02);
  border-color: rgba(255, 255, 255, 0.22);
  background-color: rgba(184, 134, 11, 0.28);
}

@media (prefers-reduced-motion: reduce) {
  .community-stat,
  .community-stat__icon,
  .community-map-frame {
    transition: none;
  }

  .community-stat:hover,
  .community-stat:hover .community-stat__icon {
    transform: none;
  }

  .home-action,
  .btn-bochas-rojo,
  .btn-bochas-azul,
  .btn-bochas-trans,
  .home-banner,
  .home-banner-image {
    transition: none;
  }

  .home-action:hover,
  .btn-bochas-rojo:hover,
  .btn-bochas-azul:hover,
  .btn-bochas-trans:hover,
  .home-banner:hover,
  .home-banner:hover .home-banner-image {
    transform: none;
  }

  .home-motion-card,
  .home-motion-image,
  .home-sponsor {
    transition: none;
  }

  .home-motion-card:hover,
  .home-motion-card:hover .home-motion-image,
  .home-sponsor:hover {
    transform: none;
  }
}
</style>
